import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";

import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { getIdFromPath } from "shared/utils";
import { EquipmentForm } from "Venues/components";
import { useVenuePageContext } from "Venues/contexts";
import {
  clearActionMeta,
  clearActionPagination,
  createEquipment,
  dataMetaSelector,
} from "Venues/state";
import { IEquipment, IPatchEquipment, VENUE_ACTIONS } from "Venues/types";
import {
  getEquipmentKey,
  getStageEquipmentKey,
  useClearActionMetaOnUnMount,
} from "Venues/utils";

const { createEquipment: createEquipmentCopy, toast } = copy;

const defaultButtons: IFormButtons = {
  primary: {
    children: createEquipmentCopy.cta,
    size: "huge",
    isFullWidth: true,
  },
};

interface CreateEquipmentProps {
  hideCategories?: boolean;
  disableStages?: string[]; // stage IDs
  defaultEquipmentValues?: IEquipment;
  fetchEquipment?: () => void;
  buttons?: IFormButtons;
}

export const CreateEquipment: React.FC<CreateEquipmentProps> = ({
  hideCategories,
  disableStages,
  defaultEquipmentValues,
  fetchEquipment,
  buttons,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<IPatchEquipment>();
  const [resetForm, setResetForm] = useState(false);

  useClearActionMetaOnUnMount(VENUE_ACTIONS.createEquipment);

  const { api: createApiMeta } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.createEquipment)
  );

  const { venueId } = useVenuePageContext();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isSubmitted && createApiMeta?.status === IAPIState.succeeded) {
      setResetForm(true);
      setIsSubmitted(false);

      dispatch(clearActionMeta(VENUE_ACTIONS.createEquipment));
      dispatch(
        addTemporaryToast({
          message: `${toast.created} ${selectedEquipment?.brand} ${selectedEquipment?.model}`,
          status: IStatus.success,
        })
      );

      // clear pagination for chosen stages/category to force fetch the overview tables
      selectedEquipment?.stages.forEach((stage) => {
        dispatch(
          clearActionPagination(
            getStageEquipmentKey(
              getIdFromPath(stage, API_ENDPOINTS.STAGES),
              selectedEquipment?.category
            )
          )
        );
      });

      // clear pagination for venue equipment overview table
      dispatch(clearActionPagination(getEquipmentKey(venueId)));

      // refetch equipment after pagination is cleared
      fetchEquipment && fetchEquipment();
    }
  }, [
    isSubmitted,
    createApiMeta,
    dispatch,
    venueId,
    selectedEquipment,
    fetchEquipment,
  ]);

  useEffect(() => {
    if (resetForm) {
      setResetForm(false);
    }
  }, [resetForm]);

  const handleSubmit = (equipment: any) => {
    const cleanEquipment =
      hideCategories && defaultEquipmentValues?.category // has default category that's not shown in form
        ? {
            ...equipment,
            category: defaultEquipmentValues?.category,
          }
        : equipment;

    const newEquipment: IEquipment = cleanEquipment;

    dispatch(createEquipment(newEquipment));

    const patchEquipment: IPatchEquipment = cleanEquipment;
    setSelectedEquipment(patchEquipment);
    setIsSubmitted(true);
  };

  return (
    <EquipmentForm
      handleSubmit={handleSubmit}
      buttons={buttons || defaultButtons}
      apiStatus={createApiMeta?.status}
      hasError={!!createApiMeta?.error}
      resetForm={resetForm}
      defaultEquipmentValues={defaultEquipmentValues}
      hideCategories={hideCategories}
      disableStages={disableStages}
    />
  );
};
